import {
  BarChartOutlined,
  BookOutlined,
  FileTextOutlined,
  LineChartOutlined,
  RadarChartOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { useValuation } from '@api/useValuation'
import { SwingReportGrid } from '@components/InventoryValuation/SwingReport/SwingReportGrid'

import OrderMovements from '@components/OrderMovements/OrderMovements/OrderMovements'
import { Forecasts } from '@components/StoreMonitoring/StoreDetailsPage/TabView/Forecast/Forecasts'
import { TankReadings } from '@components/StoreMonitoring/StoreDetailsPage/TabView/TankReadings/TankReadings'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useUserSettings } from '@hooks/settings'
import { Menu, Switch, Tooltip } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { useSecurityContext } from '../../../Navigation/SecurityContext'
import { DefaultView } from '@/pages/OrderManagement/All/page'
import { OrderManagementContextProvider } from '@/pages/OrderManagement/context'

export function StoreTabView({
  store,
  selectedTank,
  selectedManifold,
  rundownToggled,
  inventoryChartExpanded,
  toggleChartExpand,
  tankReadingData,
  isFetchingTankReadings,
}) {
  const [hasErrors, setHasErrors] = useState(false)
  const { updateSetting, getSetting } = useUserSettings()
  const { value: currentStoreTab, setValue: settingCurrentStoreTab } = useLocalStorage('StoreTabView', 'tank_orders')
  const setCurrentStoreTab = (newTab) => {
    settingCurrentStoreTab(newTab)
    updateSetting('StoreTabView', newTab)
  }

  useEffect(() => {
    getSetting('StoreTabView').then((tab) => {
      if (tab && typeof tab === 'string') setCurrentStoreTab(tab)
    })
  }, [])

  const { scopes } = useSecurityContext()

  const menuItems = [{ label: 'Site Orders', key: 'tank_orders', icon: <FileTextOutlined /> }]
  // site orders, forecasts, valuation, order movements

  if (store?.monitoring_strategy !== 'No Readings (Call-In)') {
    menuItems.push({
      label: 'Forecasts',
      key: 'forecasts',
      icon: <BarChartOutlined />,
    })
    menuItems.push({
      label: 'Tank Readings',
      key: 'tank_readings',
      icon: <LineChartOutlined />,
    })
    if (scopes?.valuation?.read) {
      menuItems.push({
        label: hasErrors ? (
          <Texto
            category='span'
            className='bg-error-dim text-error p-1 pl-2 text-size-4 round-border'
            style={{ marginLeft: -10, borderRadius: '0 5px 5px 0' }}
          >
            Swing Report <WarningFilled className='text-error' />
          </Texto>
        ) : (
          'Swing Report'
        ),
        key: 'valuation',
        icon: hasErrors ? (
          <Texto
            category='span'
            className='bg-error-dim text-error p-1 text-size-4'
            style={{ borderRadius: '5px 0 0 5px' }}
          >
            <RadarChartOutlined className='text-error' />
          </Texto>
        ) : (
          <RadarChartOutlined />
        ),
      })
    }
  }
  if (scopes?.order_movements?.view || scopes?.order_movements?.edit) {
    menuItems.push({
      label: 'Order Movements',
      key: 'order_movements',
      icon: <BookOutlined />,
    })
  }

  const onClick = (e) => {
    setCurrentStoreTab(e.key)
  }
  return (
    <Vertical flex={1} className='bordered'>
      <Horizontal alignItems='center'>
        <Menu
          onClick={onClick}
          selectedKeys={[currentStoreTab]}
          mode='horizontal'
          style={{ flex: 1, minWidth: 0 }}
          items={menuItems}
        />
        <Horizontal alignItems='center' style={{ gap: 8 }}>
          {store?.monitoring_strategy !== 'No Readings (Call-In)' && (
            <Tooltip
              title={inventoryChartExpanded ? 'Hide Inventory Chart' : 'Show Inventory Chart'}
              placement='topRight'
            >
              <Switch
                className='mr-4'
                checkedChildren={<BarChartOutlined />}
                checked={inventoryChartExpanded}
                onChange={toggleChartExpand}
                unCheckedChildren={<BarChartOutlined />}
              />
            </Tooltip>
          )}
        </Horizontal>
      </Horizontal>
      <SelectedTab
        currentStoreTab={currentStoreTab}
        store={store}
        selectedManifold={selectedManifold}
        selectedTank={selectedTank}
        rundownToggled={rundownToggled}
        setHasErrors={setHasErrors}
        hasValuationScope={scopes?.valuation?.read}
        tankReadingData={tankReadingData}
        isFetchingTankReadings={isFetchingTankReadings}
      />
    </Vertical>
  )
}

export function StoreOrderManagement({ storeNumber }) {
  return (
    <OrderManagementContextProvider>
      <DefaultView isCompact storeNumber={storeNumber} />
    </OrderManagementContextProvider>
  )
}

function SelectedTab({
  currentStoreTab,
  store,
  selectedTank,
  selectedManifold,
  rundownToggled,
  setHasErrors,
  hasValuationScope,
  tankReadingData,
  isFetchingTankReadings,
}) {
  const showTankOrders = currentStoreTab === 'tank_orders'
  const showForecasts = currentStoreTab === 'forecasts'
  const showOrderMovements = currentStoreTab === 'order_movements'
  const showValuation = hasValuationScope && currentStoreTab === 'valuation'
  const showTankReadings = currentStoreTab === 'tank_readings'
  const [valuationHeaderData, setValuationHeaderData] = useState({ site: null, product: null })

  const payload = useMemo(() => {
    const id = selectedTank?.ici?.id || selectedManifold?.ici?.id
    setValuationHeaderData({
      site: selectedTank?.store_number || selectedManifold?.store_number,
      product: selectedTank?.ici?.product || selectedManifold?.ici?.product,
    })
    if (!id) setHasErrors(false)
    return hasValuationScope ? id : null
  }, [selectedTank, selectedManifold, hasValuationScope])

  const { getHistoryByICIQuery } = useValuation()
  const {
    data: historyByICI,
    isPending: isLoadingICI,
    isFetching: isFetchingICI,
    isError: isErrorICI,
  } = getHistoryByICIQuery(payload)

  useEffect(() => {
    if (historyByICI) {
      const hasErrors = historyByICI.some((row) => row.status === 'error')
      setHasErrors(hasErrors)
    }
  }, [historyByICI])

  return (
    <Vertical flex={1}>
      {showTankOrders && <StoreOrderManagement storeNumber={store.store_number} />}
      {showForecasts && (
        <Forecasts
          selectedManifold={selectedManifold}
          selectedTank={selectedTank}
          store={store}
          showProjected={rundownToggled}
        />
      )}
      {showTankReadings && (
        <TankReadings
          storeTimezone={store.timezone}
          storeNumber={store.store_number}
          selectedTank={selectedTank}
          selectedManifold={selectedManifold}
          tankReadingData={tankReadingData}
          isFetching={isFetchingTankReadings}
        />
      )}
      {showOrderMovements && <OrderMovements isCompact storeNumber={store.store_number} />}
      {showValuation && (
        <SwingReportGrid
          historyByICI={historyByICI}
          isCompact
          isLoading={isFetchingICI}
          isError={isErrorICI}
          headerData={valuationHeaderData}
        />
      )}
    </Vertical>
  )
}
